import React, { createContext, useContext, useState, useCallback } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

// Единый механизм обратной связи: всплывающие уведомления (Snackbar)
// и диалоги подтверждения вместо браузерных alert()/confirm().

const NotifyContext = createContext(null);

export function useNotify() {
    const ctx = useContext(NotifyContext);
    if (!ctx) {
        // безопасный фолбэк, если провайдер не подключён
        return {
            notify: (_severity, message) => window.alert(message),
            confirm: ({ message }) => Promise.resolve(window.confirm(message)),
        };
    }
    return ctx;
}

export function NotificationProvider({ children }) {
    const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
    const [confirmState, setConfirmState] = useState(null);

    const notify = useCallback((severity, message) => {
        setSnack({ open: true, severity, message });
    }, []);

    const confirm = useCallback((opts) => {
        return new Promise((resolve) => {
            setConfirmState({
                title: opts.title || 'Подтверждение',
                message: opts.message || '',
                confirmText: opts.confirmText || 'Подтвердить',
                cancelText: opts.cancelText || 'Отмена',
                destructive: !!opts.destructive,
                resolve,
            });
        });
    }, []);

    const closeSnack = (_, reason) => {
        if (reason !== 'clickaway') setSnack((s) => ({ ...s, open: false }));
    };

    const resolveConfirm = (result) => {
        if (confirmState) confirmState.resolve(result);
        setConfirmState(null);
    };

    return (
        <NotifyContext.Provider value={{ notify, confirm }}>
            {children}

            <Snackbar
                open={snack.open}
                autoHideDuration={5000}
                onClose={closeSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>

            <Dialog open={!!confirmState} onClose={() => resolveConfirm(false)}>
                <DialogTitle>{confirmState?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmState?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => resolveConfirm(false)}>{confirmState?.cancelText}</Button>
                    <Button
                        variant="contained"
                        color={confirmState?.destructive ? 'error' : 'primary'}
                        onClick={() => resolveConfirm(true)}
                    >
                        {confirmState?.confirmText}
                    </Button>
                </DialogActions>
            </Dialog>
        </NotifyContext.Provider>
    );
}
